import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { analytics } from 'analytics';
import Button from 'ui/Button';
import Checkbox from 'ui/Checkbox';
import Dialog from 'ui/Dialog';
import Field from 'ui/Field';
import Icon from 'ui/Icon';
import { Flex, HStack } from 'ui/Layout';
import { toaster } from 'ui/Toaster';
import { Text } from 'ui/Typography';
import { formToSankey } from 'utils/form';

import { reportingPeriodOptions, statementTypeOptions } from '../../constants';
import { useApiContext } from '../../contexts/apiContext';
import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { pathToSankey } from '../../utils/routes';
import { useCurrentUser } from '../../utils/user';
import {
  FormNumberInput,
  FormSelect,
  FormTextInput,
  FormToggleableTextInput,
} from '../SankeyForm';
import { useNewSankeyForm } from '../SankeyFormProvider';
import { useCopyFormInitialValues } from './utils';

type Props = {
  open: boolean;
  onClose: () => void;
};

// TODO: Rename to CopyDiagramDialog and integrate with opener trigger
// TODO: Use FieldSet Chakra component
const CopyDiagramModal: React.FC<Props> = ({ open, onClose }) => {
  const { api } = useApiContext();
  const { form: originalForm } = useSankeyFormContext();
  const { refetchUser } = useCurrentUser();
  const copyFormInitialValues = useCopyFormInitialValues();
  const navigate = useNavigate();

  const [transferSharingSettings, setTransferSharingSettings] = useState(false);
  const canTransferSharingSettings =
    originalForm.getValues('public_permission') !== 'no-access' ||
    originalForm.getValues('team_permission') !== 'no-access';

  // Create a new temporary form for the copied diagram with the overriden initial values.
  // We do this so that the form values can be used and modified in the downstream input
  // components without polluting the original diagram's form.
  // We reset the form values to the initial values whenever the modal is opened.
  const copyForm = useNewSankeyForm(copyFormInitialValues);
  useEffect(() => {
    if (open) {
      copyForm.reset(copyFormInitialValues);
    }
  }, [open]);

  const { mutateAsync: makeDiagramCopy, isLoading } = useMutation(async () => {
    try {
      // Call an API to create a new diagram based on the current one and
      // refetch the user to make sure permissions are up to date.
      const { data } = await api().sankeys.create({
        sankeyRequest: {
          ...formToSankey(copyForm),
          // If opting in to transferring sharing settings, set the new diagram to the same sharing settings
          ...(transferSharingSettings
            ? {
                public_permission: originalForm.getValues('public_permission'),
                team_permission: originalForm.getValues('team_permission'),
              }
            : {}),
        },
      });
      await refetchUser();
      toaster.success({ title: 'Diagram copied successfully' });
      onClose();
      navigate(pathToSankey(data.id));
    } catch (error) {
      analytics.reportError('Error creating diagram copy', {
        updatedDiagram: copyForm.getValues(),
        originalDiagram: originalForm.getValues(),
        error,
      });
    }
  });

  const handleSubmit = copyForm.handleSubmit(() => makeDiagramCopy());

  return (
    <FormProvider {...copyForm}>
      <Dialog.Root open={open} onOpenChange={onClose}>
        <Dialog.Content>
          <form onSubmit={handleSubmit}>
            <Dialog.Header>
              <Icon fontSize="md" icon="copy-regular" mr={2} /> Create a diagram
              copy
            </Dialog.Header>
            <Dialog.Body>
              <Flex alignItems="stretch" direction="column" gap={2}>
                <Text fontSize="sm" mb={2}>
                  Please confirm the details of the new diagram copy you are
                  about to create:
                </Text>
                <FormTextInput label="Company name" field="company_name" />
                <HStack>
                  <FormNumberInput field="fiscal_year" label="Fiscal year" />
                  <Field label="Reporting period">
                    <FormSelect
                      field="reporting_period"
                      options={reportingPeriodOptions}
                    />
                  </Field>
                </HStack>
                <Field label="Financial statement type">
                  <FormSelect
                    field="statement_type"
                    options={statementTypeOptions}
                  />
                </Field>
                <FormToggleableTextInput
                  label="Use custom title"
                  checkboxField="use_custom_title"
                  textField="custom_title"
                />
                {canTransferSharingSettings && (
                  <Field label="Sharing settings" mt={4}>
                    <Checkbox
                      checked={transferSharingSettings}
                      onCheckedChange={(e) =>
                        setTransferSharingSettings(!!e.checked)
                      }
                    >
                      I want to copy my sharing settings too
                    </Checkbox>
                  </Field>
                )}
              </Flex>
            </Dialog.Body>
            <Dialog.Footer>
              <Button
                type="submit"
                variant="primary-solid"
                loading={isLoading}
                disabled={!copyForm.formState.isValid}
              >
                Create a copy
              </Button>
            </Dialog.Footer>
          </form>
        </Dialog.Content>
      </Dialog.Root>
    </FormProvider>
  );
};

export default CopyDiagramModal;
